import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// Configuration - set to true to use mock data instead of API
const USE_MOCK_DATA = false;

// Types
export interface AdUser {
  username: string;
  name: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  description?: string | null;
  enabled: boolean;
  locked?: boolean;
  groups: string[];
  dn?: string;
  objectGUID?: string;
  createdAt?: string;
  modifiedAt?: string;
  lastLogon?: string;
  department?: string;
}

export interface AdGroup {
  name: string;
  members: string[];
  memberCount: number;
  description?: string | null;
}

export interface AdUserSummary {
  totalUsers: number;
  enabledUsers: number;
  disabledUsers: number;
  lockedUsers: number;
  totalGroups: number;
}

export interface CreateAdUserInput {
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  email?: string;
  description?: string;
  ou?: string;
  groups?: string[];
  mustChangePassword?: boolean;
}

export interface UpdateAdUserInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  description?: string;
  enabled?: boolean;
}

export interface ResetPasswordInput {
  userId: string;
  newPassword: string;
  mustChangePassword?: boolean;
}

export interface CreateAdGroupInput {
  name: string;
  description?: string;
  ou?: string;
}

export interface CreateAdOuInput {
  dn: string;
  description?: string;
}

export interface AdUsersResponse {
  users: AdUser[];
  total: number;
  summary: AdUserSummary;
}

export interface AdGroupsResponse {
  groups: AdGroup[];
  total: number;
}

// Query keys
export const adQueryKeys = {
  users: {
    all: ['ad', 'users'] as const,
    list: (filters?: AdUserFilters) => ['ad', 'users', 'list', filters] as const,
    detail: (id: string) => ['ad', 'users', 'detail', id] as const,
  },
  groups: {
    all: ['ad', 'groups'] as const,
    list: () => ['ad', 'groups', 'list'] as const,
    members: (id: string) => ['ad', 'groups', 'members', id] as const,
  },
  summary: ['ad', 'summary'] as const,
};

export interface AdUserFilters {
  search?: string;
  enabled?: boolean;
  locked?: boolean;
  group?: string;
  ou?: string;
}

// API endpoints
const adEndpoints = {
  users: '/api/v1/ad/users',
  user: (username: string) => `/api/v1/ad/users/${username}`,
  createUser: '/api/v1/ad/users',
  updateUser: (username: string) => `/api/v1/ad/users/${username}`,
  deleteUser: (username: string) => `/api/v1/ad/users/${username}`,
  resetPassword: (username: string) => `/api/v1/ad/users/${username}/password`,
  enableUser: (username: string) => `/api/v1/ad/users/${username}/enable`,
  disableUser: (username: string) => `/api/v1/ad/users/${username}/disable`,
  unlockUser: (username: string) => `/api/v1/ad/users/${username}/unlock`,
  addToGroup: (username: string, groupName: string) => `/api/v1/ad/users/${username}/groups/${groupName}`,
  removeFromGroup: (username: string, groupName: string) => `/api/v1/ad/users/${username}/groups/${groupName}`,
  groups: '/api/v1/ad/groups',
  createGroup: '/api/v1/ad/groups',
  deleteGroup: (groupName: string) => `/api/v1/ad/groups/${groupName}`,
  groupMembers: (groupName: string) => `/api/v1/ad/groups/${groupName}/members`,
  summary: '/api/v1/ad/summary',
  taskResult: (taskId: string) => `/api/v1/ad/tasks/${taskId}`,
  ous: '/api/v1/ad/ous',
  createOu: '/api/v1/ad/ous',
  deleteOu: (ouDn: string) => `/api/v1/ad/ous/${encodeURIComponent(ouDn)}`,
};

// Mock data for development (used when USE_MOCK_DATA is true or API is unavailable)
const mockUsers: AdUser[] = [
  {
    username: 'ucenec1',
    name: 'Ucenec Ena',
    displayName: 'Ucenec Ena',
    firstName: 'Ucenec',
    lastName: 'Ena',
    email: 'ucenec1@sola.si',
    description: 'Ucenec 1. razreda',
    enabled: true,
    locked: false,
    groups: ['Ucenci', '1-Razred'],
    dn: 'CN=Ucenec Ena,OU=Ucenci,DC=sola,DC=local',
    createdAt: '2024-01-15T10:00:00Z',
    modifiedAt: '2024-06-01T14:00:00Z',
    lastLogon: '2024-06-05T08:30:00Z',
  },
  {
    username: 'ucitelj1',
    name: 'Janez Novak',
    displayName: 'Janez Novak',
    firstName: 'Janez',
    lastName: 'Novak',
    email: 'janez.novak@sola.si',
    description: 'Ucitelj matematike',
    enabled: true,
    locked: false,
    groups: ['Ucitelji', 'Administracija'],
    dn: 'CN=Janez Novak,OU=Ucitelji,DC=sola,DC=local',
    createdAt: '2023-09-01T09:00:00Z',
    modifiedAt: '2024-05-15T11:00:00Z',
    lastLogon: '2024-06-05T07:45:00Z',
  },
  {
    username: 'admin',
    name: 'Administrator',
    displayName: 'Administrator',
    firstName: 'Admin',
    lastName: 'Sistem',
    email: 'admin@sola.si',
    description: 'Sistemski administrator',
    enabled: true,
    locked: false,
    groups: ['Domain Admins', 'Administrators'],
    dn: 'CN=Administrator,OU=Admins,DC=sola,DC=local',
    createdAt: '2020-01-01T00:00:00Z',
    modifiedAt: '2024-06-04T16:00:00Z',
    lastLogon: '2024-06-04T16:00:00Z',
  },
];

const mockGroups: AdGroup[] = [
  { name: 'Ucenci', members: [], memberCount: 150, description: 'Vsi ucenci' },
  { name: 'Ucitelji', members: [], memberCount: 25, description: 'Vsi ucitelji' },
  { name: '1-Razred', members: [], memberCount: 30, description: 'Ucenci 1. razreda' },
  { name: 'Domain Admins', members: [], memberCount: 3, description: 'Domain administrators' },
];

// Helper to filter mock users
function filterMockUsers(filters?: AdUserFilters): AdUser[] {
  let filteredUsers = [...mockUsers];

  if (filters?.search) {
    const search = filters.search.toLowerCase();
    filteredUsers = filteredUsers.filter(u =>
      u.username.toLowerCase().includes(search) ||
      u.name.toLowerCase().includes(search) ||
      u.displayName?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search)
    );
  }

  if (filters?.enabled !== undefined) {
    filteredUsers = filteredUsers.filter(u => u.enabled === filters.enabled);
  }

  if (filters?.locked !== undefined) {
    filteredUsers = filteredUsers.filter(u => u.locked === filters.locked);
  }

  if (filters?.group) {
    filteredUsers = filteredUsers.filter(u => u.groups.includes(filters.group!));
  }

  return filteredUsers;
}

// Helper to calculate summary
function calculateMockSummary(): AdUserSummary {
  return {
    totalUsers: mockUsers.length,
    enabledUsers: mockUsers.filter(u => u.enabled).length,
    disabledUsers: mockUsers.filter(u => !u.enabled).length,
    lockedUsers: mockUsers.filter(u => u.locked).length,
    totalGroups: mockGroups.length,
  };
}

/**
 * Hook to fetch AD users with optional filters
 */
export function useAdUsers(filters?: AdUserFilters) {
  return useQuery({
    queryKey: adQueryKeys.users.list(filters),
    queryFn: async (): Promise<AdUsersResponse> => {
      if (USE_MOCK_DATA) {
        const filteredUsers = filterMockUsers(filters);
        return {
          users: filteredUsers,
          total: filteredUsers.length,
          summary: calculateMockSummary(),
        };
      }

      // Try real API
      try {
        const params: Record<string, string> = {};
        if (filters?.search) params.search = filters.search;
        if (filters?.enabled !== undefined) params.enabled = String(filters.enabled);
        if (filters?.locked !== undefined) params.locked = String(filters.locked);
        if (filters?.group) params.group = filters.group;

        const response = await api.get<AdUsersResponse>(adEndpoints.users, { params });
        return response;
      } catch {
        // Fallback to mock data
        const filteredUsers = filterMockUsers(filters);
        return {
          users: filteredUsers,
          total: filteredUsers.length,
          summary: calculateMockSummary(),
        };
      }
    },
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single AD user
 */
export function useAdUser(userId: string) {
  return useQuery({
    queryKey: adQueryKeys.users.detail(userId),
    queryFn: async (): Promise<AdUser> => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        if (!user) throw new Error('User not found');
        return user;
      }

      try {
        return await api.get<AdUser>(adEndpoints.user(userId));
      } catch {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        if (!user) throw new Error('User not found');
        return user;
      }
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch AD groups
 */
export function useAdGroups() {
  return useQuery({
    queryKey: adQueryKeys.groups.list(),
    queryFn: async (): Promise<AdGroupsResponse> => {
      if (USE_MOCK_DATA) {
        return {
          groups: mockGroups,
          total: mockGroups.length,
        };
      }

      try {
        return await api.get<AdGroupsResponse>(adEndpoints.groups);
      } catch {
        return {
          groups: mockGroups,
          total: mockGroups.length,
        };
      }
    },
    staleTime: 60000,
  });
}

/**
 * Hook to fetch AD summary
 */
export function useAdSummary() {
  return useQuery({
    queryKey: adQueryKeys.summary,
    queryFn: async (): Promise<AdUserSummary> => {
      if (USE_MOCK_DATA) {
        return calculateMockSummary();
      }

      try {
        return await api.get<AdUserSummary>(adEndpoints.summary);
      } catch {
        return calculateMockSummary();
      }
    },
    staleTime: 30000,
  });
}

/**
 * Hook to create a new AD user
 */
export function useCreateAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAdUserInput) => {
      if (USE_MOCK_DATA) {
        const newUser: AdUser = {
          username: input.username,
          name: `${input.firstName} ${input.lastName}`,
          displayName: `${input.firstName} ${input.lastName}`,
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          description: input.description,
          enabled: true,
          locked: false,
          groups: input.groups || [],
          dn: `CN=${input.firstName} ${input.lastName},${input.ou || 'OU=Users,DC=sola,DC=local'}`,
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        };
        mockUsers.push(newUser);
        return { user: newUser, taskId: 'mock-task-' + Date.now() };
      }

      return api.post<{ taskId: string }>(adEndpoints.createUser, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to update an AD user
 */
export function useUpdateAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateAdUserInput }) => {
      if (USE_MOCK_DATA) {
        const existingUser = mockUsers.find(u => u.username === userId || u.username === userId);
        if (!existingUser) {
          throw new Error('User not found');
        }

        const firstName = data.firstName ?? existingUser.firstName;
        const lastName = data.lastName ?? existingUser.lastName;
        const displayName = (data.firstName || data.lastName)
          ? `${firstName} ${lastName}`
          : existingUser.displayName;

        const updatedUser: AdUser = {
          ...existingUser,
          ...data,
          displayName,
          modifiedAt: new Date().toISOString(),
        } as AdUser;

        const userIndex = mockUsers.findIndex(u => u.username === userId || u.username === userId);
        mockUsers[userIndex] = updatedUser;
        return { user: updatedUser, taskId: 'mock-task-' + Date.now() };
      }

      return api.patch<{ taskId: string }>(adEndpoints.updateUser(userId), data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.detail(variables.userId) });
    },
  });
}

/**
 * Hook to delete an AD user
 */
export function useDeleteAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (USE_MOCK_DATA) {
        const index = mockUsers.findIndex(u => u.username === userId || u.username === userId);
        if (index >= 0) {
          mockUsers.splice(index, 1);
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User not found');
      }

      return api.delete<{ taskId: string }>(adEndpoints.deleteUser(userId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to reset user password
 */
export function useResetAdPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ResetPasswordInput) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === input.userId || u.username === input.userId);
        if (user) {
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User not found');
      }

      return api.post<{ taskId: string }>(adEndpoints.resetPassword(input.userId), {
        newPassword: input.newPassword,
        mustChangePassword: input.mustChangePassword,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.detail(variables.userId) });
    },
  });
}

/**
 * Hook to enable an AD user
 */
export function useEnableAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        if (user) {
          user.enabled = true;
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User not found');
      }

      return api.post<{ taskId: string }>(adEndpoints.enableUser(userId), {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to disable an AD user
 */
export function useDisableAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        if (user) {
          user.enabled = false;
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User not found');
      }

      return api.post<{ taskId: string }>(adEndpoints.disableUser(userId), {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to unlock an AD user
 */
export function useUnlockAdUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        if (user) {
          user.locked = false;
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User not found');
      }

      return api.post<{ taskId: string }>(adEndpoints.unlockUser(userId), {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to add user to group
 */
export function useAddUserToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, groupId }: { userId: string; groupId: string }) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        const group = mockGroups.find(g => g.name === groupId || g.name === groupId);
        if (user && group && !user.groups.includes(group.name)) {
          user.groups.push(group.name);
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User or group not found');
      }

      return api.post<{ taskId: string }>(adEndpoints.addToGroup(userId, groupId), {});
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.members(variables.groupId) });
    },
  });
}

/**
 * Hook to remove user from group
 */
export function useRemoveUserFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, groupId }: { userId: string; groupId: string }) => {
      if (USE_MOCK_DATA) {
        const user = mockUsers.find(u => u.username === userId || u.username === userId);
        const group = mockGroups.find(g => g.name === groupId || g.name === groupId);
        if (user && group) {
          user.groups = user.groups.filter(g => g !== group.name);
          user.modifiedAt = new Date().toISOString();
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('User or group not found');
      }

      return api.delete<{ taskId: string }>(adEndpoints.removeFromGroup(userId, groupId));
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.members(variables.groupId) });
    },
  });
}

/**
 * Hook to fetch group members
 */
export function useAdGroupMembers(groupId: string) {
  return useQuery({
    queryKey: adQueryKeys.groups.members(groupId),
    queryFn: async () => {
      if (USE_MOCK_DATA) {
        const group = mockGroups.find(g => g.name === groupId || g.name === groupId);
        if (!group) throw new Error('Group not found');
        const members = mockUsers.filter(u => u.groups.includes(group.name));
        return { members };
      }

      return api.get<{ members: AdUser[] }>(adEndpoints.groupMembers(groupId));
    },
    enabled: !!groupId,
  });
}

/**
 * Hook to create a new AD group
 */
export function useCreateAdGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAdGroupInput) => {
      if (USE_MOCK_DATA) {
        const newGroup: AdGroup = {
          name: input.name,
          description: input.description,
          members: [],
          memberCount: 0,
        };
        mockGroups.push(newGroup);
        return { group: newGroup, taskId: 'mock-task-' + Date.now() };
      }

      return api.post<{ taskId: string }>(adEndpoints.createGroup, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to delete an AD group
 */
export function useDeleteAdGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupName: string) => {
      if (USE_MOCK_DATA) {
        const index = mockGroups.findIndex(g => g.name === groupName);
        if (index >= 0) {
          mockGroups.splice(index, 1);
          return { success: true, taskId: 'mock-task-' + Date.now() };
        }
        throw new Error('Group not found');
      }

      return api.delete<{ taskId: string }>(adEndpoints.deleteGroup(groupName));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to create a new Organizational Unit (OU)
 */
export function useCreateAdOu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAdOuInput) => {
      if (USE_MOCK_DATA) {
        return { success: true, dn: input.dn, taskId: 'mock-task-' + Date.now() };
      }

      return api.post<{ taskId: string }>(adEndpoints.createOu, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}

/**
 * Hook to delete an Organizational Unit (OU)
 */
export function useDeleteAdOu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ouDn: string) => {
      if (USE_MOCK_DATA) {
        return { success: true, taskId: 'mock-task-' + Date.now() };
      }

      return api.delete<{ taskId: string }>(adEndpoints.deleteOu(ouDn));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adQueryKeys.groups.all });
      queryClient.invalidateQueries({ queryKey: adQueryKeys.summary });
    },
  });
}
